import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Send, ArrowRight, Check, Sparkles, Users, FileText, Zap, TrendingUp, Clock, AlertCircle, DollarSign, Activity } from "lucide-react";
import { invoiceService } from "../services/invoice.service";
import { clientService } from "../services/client.service";
import { formatCurrency } from "../utils/formatCurrency";
import settingsService from "../services/settings.service";
import { 
  MetricCard, 
  InsightCard, 
  ActionCenter, 
  ActivityTimeline, 
  DraftInvoicesWidget, 
  RevenueChart 
} from "../features/dashboard/components";
import useDashboardMetrics from "../features/dashboard/hooks/useDashboardMetrics";
import useBusinessInsights from "../features/dashboard/hooks/useBusinessInsights";
import { generateRevenueTrendData } from "../features/dashboard/utils/calculations";

export default function Dashboard({ onNavigateToInvoices }) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]); // Store all invoices for hooks
  const [metrics, setMetrics] = useState({
    totalOutstanding: 0,
    revenueThisMonth: 0,
    overdueCount: 0,
    draftCount: 0,
    revenueChange: 0,
  });
  const [nextActions, setNextActions] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [draftInvoices, setDraftInvoices] = useState([]);
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardState, setDashboardState] = useState('new'); // 'new' | 'early' | 'active' | 'power'
  const [showCompletionFeedback, setShowCompletionFeedback] = useState(null); // For progress feedback
  const [ctaLoading, setCTALoading] = useState(false); // For CTA loading state
  const [onboardingProgress, setOnboardingProgress] = useState({
    hasClients: false,
    hasInvoices: false,
    hasSentInvoices: false,
    hasAccountSetup: false, // New field for account setup validation
    totalInvoicesSent: 0,
    recentActivityCount: 0,
    completedSteps: 0,
    totalSteps: 4,
    currentStep: null,
    progressPercentage: 0,
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Calculate enhanced metrics using new hooks (must be at top level)
  const enhancedMetrics = useDashboardMetrics(invoices);
  const businessInsights = useBusinessInsights(invoices);
  const revenueTrendData = generateRevenueTrendData(invoices, 6);

  // Smart Next Step Logic - Enhanced with nested account setup
  const getSmartNextStep = () => {
    const { hasAccountSetup, hasClients, hasInvoices, hasSentInvoices, accountSetupResult } = onboardingProgress;
    
    const handleCTAClick = (navigationFn) => {
      setCTALoading(true);
      setTimeout(() => {
        navigationFn();
        setCTALoading(false);
      }, 300); // Brief loading state for smooth UX
    };
    
    if (!hasAccountSetup) {
      // Return nested account setup instead of simple CTA
      return {
        id: 'setup-account',
        title: 'Complete your account setup',
        description: `Finish your business settings to start sending invoices`,
        isNested: true,
        accountSetupResult,
        action: 'Continue Setup',
        onClick: () => handleCTAClick(() => navigate('/settings#company'))
      };
    }
    
    if (!hasClients) {
      return {
        id: 'add-client',
        title: 'Add your first client',
        description: 'Add your first client to start creating invoices and getting paid faster',
        action: 'Add Client',
        onClick: () => handleCTAClick(() => navigate('/clients?action=create'))
      };
    }
    
    if (!hasInvoices) {
      return {
        id: 'create-invoice',
        title: 'Create your first invoice',
        description: 'Create a professional invoice to get paid faster',
        action: 'Create Invoice',
        onClick: () => handleCTAClick(() => navigate('/invoices?action=create'))
      };
    }
    
    if (!hasSentInvoices) {
      return {
        id: 'send-invoice',
        title: 'Send your first invoice',
        description: 'Send your invoice to start receiving payments',
        action: 'Send Invoice',
        onClick: () => handleCTAClick(() => navigate('/invoices'))
      };
    }
    
    return null;
  };
  
  // Account Setup Validation with Nested Progress
  const validateAccountSetup = (theme, smtp) => {
    // 1. Company Info (Required) - Must be customized beyond defaults
    const hasCustomCompanyName = theme.company.name && 
                                 theme.company.name.trim() !== "" && 
                                 theme.company.name !== "ZyloBill";
    const hasAddress = theme.company.address && theme.company.address.trim() !== "";
    const hasCompanyEmail = theme.company.email && theme.company.email.trim() !== "";
    const hasCompanyInfo = hasCustomCompanyName && hasAddress && hasCompanyEmail;
    
    // 2. Email Delivery (Required) - Must be properly configured
    const hasEmailDelivery = smtp.enabled && 
                            smtp.smtp_host && smtp.smtp_host.trim() !== "" &&
                            smtp.smtp_user && smtp.smtp_user.trim() !== "" &&
                            smtp.smtp_password && smtp.smtp_password.trim() !== "" &&
                            smtp.from_email && smtp.from_email.trim() !== "";
    
    // 3. Branding (Optional) - Must have custom logo OR custom brand color
    const hasCustomLogo = theme.logoUrl && theme.logoUrl !== null && theme.logoUrl.trim() !== "";
    const hasCustomBrandColor = theme.brandColor && 
                               theme.brandColor !== "#6366f1" && 
                               theme.brandColor.trim() !== "";
    const hasBranding = hasCustomLogo || hasCustomBrandColor;
    
    // 4. Invoice Layout (Optional) - Must have customized settings
    const hasCustomGstSettings = theme.showGstBreakdown !== true || theme.showHsn !== true;
    const hasInvoiceLayout = hasCustomGstSettings || theme.showSignature === true;
    
    // 5. Footer & Terms (Optional) - Must have actual content
    const hasPaymentInstructions = theme.paymentInstructions && 
                                  theme.paymentInstructions.trim() !== "";
    const hasTermsConditions = theme.termsAndConditions && 
                              theme.termsAndConditions.trim() !== "" &&
                              theme.termsAndConditions !== "This is a computer-generated invoice and does not require a signature.";
    const hasFooterTerms = hasPaymentInstructions || hasTermsConditions;
    
    const accountSubTasks = [
      { id: 'company', label: 'Company Info', completed: hasCompanyInfo, required: true },
      { id: 'email', label: 'Email Delivery', completed: hasEmailDelivery, required: true },
      { id: 'branding', label: 'Branding', completed: hasBranding, required: false },
      { id: 'layout', label: 'Invoice Layout', completed: hasInvoiceLayout, required: false },
      { id: 'footer', label: 'Footer & Terms', completed: hasFooterTerms, required: false },
    ];
    
    const completedTasks = accountSubTasks.filter(task => task.completed).length;
    const requiredTasks = accountSubTasks.filter(task => task.required);
    const completedRequiredTasks = requiredTasks.filter(task => task.completed).length;
    const accountProgress = Math.round((completedTasks / accountSubTasks.length) * 100);
    
    // Account is complete when all required tasks are done
    const isAccountComplete = completedRequiredTasks === requiredTasks.length;
    
    return {
      isComplete: isAccountComplete,
      progress: accountProgress,
      subTasks: accountSubTasks,
      completedTasks,
      totalTasks: accountSubTasks.length
    };
  };
  
  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      const [invoicesData, clients, theme, smtp] = await Promise.all([
        invoiceService.list({}),
        clientService.list(),
        settingsService.getActiveTheme(),
        settingsService.getSmtpSettings(),
      ]);
      
      // Store invoices for hooks
      setInvoices(invoicesData);
      
      // Calculate onboarding progress
      const sentInvoices = invoicesData.filter(inv => inv.status !== 'draft');
      const hasClients = clients.length > 0;
      const hasInvoices = invoicesData.length > 0;
      const hasSentInvoices = sentInvoices.length > 0;
      const accountSetupResult = validateAccountSetup(theme, smtp);
      const hasAccountSetup = accountSetupResult.isComplete;
      const totalInvoicesSent = sentInvoices.length;
      
      // Count recent activity (last 30 days)
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const recentActivityCount = invoicesData.filter(inv => 
        new Date(inv.created_at) >= thirtyDaysAgo && inv.status !== 'draft'
      ).length;

      // Calculate completion steps
      const steps = [
        { id: 'account', completed: hasAccountSetup },
        { id: 'client', completed: hasClients },
        { id: 'invoice', completed: hasInvoices },
        { id: 'send', completed: hasSentInvoices },
      ];
      
      const completedSteps = steps.filter(step => step.completed).length;
      const progressPercentage = Math.round((completedSteps / steps.length) * 100);

      // Progress feedback - show completion message
      const prevCompletedSteps = onboardingProgress.completedSteps || 0;
      if (completedSteps > prevCompletedSteps) {
        const justCompleted = steps[completedSteps - 1];
        if (justCompleted && justCompleted.id !== 'account') { // Don't show for initial account step
          const stepLabels = {
            'client': 'Client',
            'invoice': 'Invoice', 
            'send': 'First invoice sent'
          };
          setShowCompletionFeedback(stepLabels[justCompleted.id] + ' completed');
          setTimeout(() => setShowCompletionFeedback(null), 3000);
        }
      }

      // Enhanced completion feedback with intelligent messaging
      const prevAccountProgress = onboardingProgress.accountSetupResult?.progress || 0;
      if (accountSetupResult.progress > prevAccountProgress) {
        // Show specific task completion
        const newlyCompleted = accountSetupResult.subTasks.find(task => 
          task.completed && 
          !(onboardingProgress.accountSetupResult?.subTasks.find(t => t.id === task.id)?.completed)
        );
        
        if (newlyCompleted) {
          setShowCompletionFeedback(`${newlyCompleted.label} completed`);
        } else if (accountSetupResult.progress < 100) {
          setShowCompletionFeedback(`Account setup ${accountSetupResult.progress}% complete`);
        }
        setTimeout(() => setShowCompletionFeedback(null), 3500);
      } else if (accountSetupResult.isComplete && !onboardingProgress.hasAccountSetup) {
        setShowCompletionFeedback('Account setup complete! Ready for clients');
        setTimeout(() => setShowCompletionFeedback(null), 4000);
      }

      // Determine dashboard state
      let state = 'new';
      if (totalInvoicesSent >= 5 || recentActivityCount >= 3) {
        state = 'power';
      } else if (hasSentInvoices) {
        state = 'active';
      } else if (hasInvoices) {
        state = 'early';
      }
      
      setDashboardState(state);
      setOnboardingProgress({
        hasClients,
        hasInvoices,
        hasSentInvoices,
        hasAccountSetup,
        accountSetupResult, // Add the detailed account setup data
        totalInvoicesSent,
        recentActivityCount,
        completedSteps,
        totalSteps: steps.length,
        progressPercentage,
      });

      // For new/early users, skip complex metrics
      if (state === 'new' || state === 'early') {
        const draftCount = invoicesData.filter(inv => inv.status === 'draft').length;
        setMetrics(prev => ({ ...prev, draftCount }));
        setDraftInvoices(invoicesData.filter(inv => inv.status === 'draft').slice(0, 5));
        setIsLoading(false);
        return;
      }
      
      // Full metrics calculation for active/power users
      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      
      let totalOutstanding = 0;
      let revenueThisMonth = 0;
      let revenueLastMonth = 0;
      let overdueCount = 0;
      let draftCount = 0;
      
      const drafts = [];
      const overdue = [];
      const activity = [];
      const actions = [];

      invoicesData.forEach(invoice => {
        if ((invoice.status === 'sent' || invoice.status === 'partially_paid') && invoice.balance_due > 0) {
          totalOutstanding += invoice.balance_due;
        }

        if (invoice.status === 'paid') {
          const paidDate = new Date(invoice.updated_at);
          if (paidDate >= thisMonthStart) {
            revenueThisMonth += invoice.total;
          }
          if (paidDate >= lastMonthStart && paidDate <= lastMonthEnd) {
            revenueLastMonth += invoice.total;
          }
        }

        if ((invoice.status === 'sent' || invoice.status === 'partially_paid') && new Date(invoice.due_date) < now && invoice.balance_due > 0) {
          overdueCount++;
          overdue.push(invoice);
        }

        if (invoice.status === 'draft') {
          draftCount++;
          drafts.push(invoice);
        }

        if (invoice.status === 'paid' || invoice.status === 'sent' || invoice.status === 'partially_paid') {
          activity.push({
            type: invoice.status === 'paid' ? 'payment' : 'sent',
            invoice: invoice.invoice_number,
            client: invoice.client_name,
            amount: invoice.total,
            timestamp: invoice.updated_at,
          });
        }
      });

      const revenueChange = revenueLastMonth > 0 
        ? ((revenueThisMonth - revenueLastMonth) / revenueLastMonth) * 100 
        : 0;

      // Generate smart actions
      if (overdueCount > 0) {
        const overdueTotal = overdue.reduce((sum, inv) => sum + inv.balance_due, 0);
        actions.push({
          id: 'overdue',
          message: `${overdueCount} ${overdueCount === 1 ? 'invoice' : 'invoices'} overdue`,
          action: 'Follow Up',
          type: 'urgent',
          context: `${formatCurrency(overdueTotal)} needs collection`,
        });
      }

      if (draftCount > 0) {
        actions.push({
          id: 'drafts',
          message: `${draftCount} ${draftCount === 1 ? 'draft' : 'drafts'} ready to send`,
          action: 'Review & Send',
          type: 'action',
          context: 'Convert drafts to revenue',
        });
      }

      activity.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      setMetrics({
        totalOutstanding,
        revenueThisMonth,
        overdueCount,
        draftCount,
        revenueChange,
      });

      setNextActions(actions);
      setRecentActivity(activity.slice(0, 6));
      setDraftInvoices(drafts.slice(0, 5));
      setOverdueInvoices(overdue.slice(0, 5));
    } catch (err) {
      console.error("Failed to load dashboard data:", err);
    } finally {
      setIsLoading(false);
    }
  };
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <div className="text-slate-600">Loading your dashboard...</div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // ONBOARDING STATES (NEW & EARLY USERS) - FINAL POLISH
  // ═══════════════════════════════════════════════════════════
  if (dashboardState === 'new' || dashboardState === 'early') {
    const smartNextStep = getSmartNextStep();

    return (
      <div className="h-full overflow-auto bg-white">
        {/* Minimal Progress Feedback Toast */}
        {showCompletionFeedback && (
          <div className="fixed top-6 right-6 z-50 animate-in slide-in-from-top-2 duration-300">
            <div className="bg-slate-900 text-white px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-3 min-w-[220px]">
              <div className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0"></div>
              <span>{showCompletionFeedback}</span>
            </div>
          </div>
        )}
        
        <div className="max-w-3xl mx-auto px-6 py-16">
          
          {/* Hero Section - Minimal */}
          <div className="text-center mb-20">
            <h1 className="text-3xl font-semibold text-slate-900 mb-3">
              {dashboardState === 'new' 
                ? "Welcome to ZyloBill" 
                : "You're making progress"
              }
            </h1>
            
            <p className="text-lg text-slate-600 mb-16">
              {dashboardState === 'new'
                ? "Let's get your billing set up in a few simple steps"
                : "Complete your setup to start getting paid"
              }
            </p>

            {/* Connected Stepper with Enhanced Progress */}
            <div className="mb-3">
              <div className="flex items-center justify-between max-w-lg mx-auto">
                {[
                  { id: 'account', label: 'Account', completed: onboardingProgress.hasAccountSetup },
                  { id: 'client', label: 'Client', completed: onboardingProgress.hasClients },
                  { id: 'invoice', label: 'Invoice', completed: onboardingProgress.hasInvoices },
                  { id: 'send', label: 'Send', completed: onboardingProgress.hasSentInvoices },
                ].map((step, index) => {
                  const isCurrent = index === onboardingProgress.completedSteps;
                  const isActive = !step.completed && isCurrent;
                  
                  return (
                    <div key={step.id} className="flex items-center">
                      <div className="flex flex-col items-center">
                        <button
                          onClick={() => {
                            if (step.id === 'account') {
                              navigate('/settings#company');
                            } else if (step.id === 'client') {
                              navigate('/clients?action=create');
                            } else if (step.id === 'invoice') {
                              navigate('/invoices?action=create');
                            } else if (step.id === 'send') {
                              navigate('/invoices');
                            }
                          }}
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ease-out hover:scale-110 ${
                            step.completed 
                              ? 'bg-slate-500 text-white hover:bg-slate-600' 
                              : isActive
                              ? 'bg-slate-800 text-white shadow-sm hover:bg-slate-900' 
                              : 'bg-slate-200 text-slate-400 hover:bg-slate-300'
                          }`}
                        >
                          {step.completed ? (
                            <Check size={14} strokeWidth={2.5} />
                          ) : (
                            index + 1
                          )}
                        </button>
                        <span className={`text-xs mt-3 font-medium transition-all duration-300 ease-out ${
                          step.completed 
                            ? 'text-slate-500' 
                            : isActive
                            ? 'text-slate-900 font-semibold' 
                            : 'text-slate-400'
                        }`}>
                          {step.label}
                        </span>
                      </div>
                      {index < 3 && (
                        <div className="relative w-20 h-px mx-5">
                          {/* Background line */}
                          <div className="absolute inset-0 bg-slate-200"></div>
                          {/* Progress fill with smooth animation */}
                          <div 
                            className={`absolute inset-0 bg-slate-400 transition-all duration-1000 ease-out ${
                              index < onboardingProgress.completedSteps ? 'w-full opacity-100' : 'w-0 opacity-0'
                            }`}
                          ></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Step Text - Refined Hierarchy */}
            <div className="text-xs text-slate-400 font-normal">
              Step {onboardingProgress.completedSteps} of 4 complete
            </div>
          </div>

          {/* Primary Action Card - Enhanced with Nested Account Setup */}
          {smartNextStep && (
            <div className="max-w-xl mx-auto mb-8">
              {smartNextStep.isNested ? (
                /* Refined Account Setup Card - High-end SaaS UX */
                <div className="bg-slate-50 rounded-xl px-7 py-7 border border-slate-200">
                  <div className="text-center mb-5">
                    <h2 className="text-xl font-semibold text-slate-900 mb-2">
                      {smartNextStep.title}
                    </h2>
                    <p className="text-slate-600 mb-1">
                      {smartNextStep.description}
                    </p>
                    <p className="text-sm text-slate-500">
                      Required to start sending invoices
                    </p>
                    
                    {/* Enhanced Progress Indicator */}
                    <div className="mb-5 mt-4">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-slate-600 font-medium">
                          {smartNextStep.accountSetupResult.completedTasks} of {smartNextStep.accountSetupResult.totalTasks} completed
                        </span>
                        <span className="text-slate-500">
                          {smartNextStep.accountSetupResult.progress}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-3">
                        <div 
                          className="bg-slate-600 h-3 rounded-full transition-all duration-1000 ease-out"
                          style={{ width: `${smartNextStep.accountSetupResult.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  {/* Simplified Account Setup Checklist */}
                  <div className="space-y-2 mb-6">
                    {smartNextStep.accountSetupResult.subTasks.map((task, index) => {
                      const isNextRequired = !task.completed && task.required && 
                        smartNextStep.accountSetupResult.subTasks
                          .filter(t => t.required && !t.completed)
                          .indexOf(task) === 0;
                      
                      const getActionLabel = (taskId, completed) => {
                        if (completed) return 'Edit';
                        const labels = {
                          company: 'Complete',
                          email: 'Configure', 
                          branding: 'Customize',
                          layout: 'Configure',
                          footer: 'Add'
                        };
                        return labels[taskId] || 'Setup';
                      };

                      return (
                        <button
                          key={task.id}
                          onClick={() => {
                            setCTALoading(true);
                            setTimeout(() => {
                              const sectionMap = {
                                company: 'company',
                                email: 'email', 
                                branding: 'branding',
                                layout: 'layout',
                                footer: 'footer'
                              };
                              navigate(`/settings#${sectionMap[task.id]}`);
                              setCTALoading(false);
                            }, 200);
                          }}
                          className={`w-full flex items-center justify-between p-3.5 rounded-lg border transition-all duration-200 group cursor-pointer ${
                            isNextRequired 
                              ? 'bg-slate-100 border-slate-300 hover:border-slate-400 hover:bg-slate-150' 
                              : task.completed
                              ? 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                              : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {/* Simplified State Indicators */}
                            <div className={`w-4 h-4 rounded-full flex items-center justify-center transition-all ${
                              task.completed 
                                ? 'bg-slate-500 text-white' 
                                : isNextRequired
                                ? 'bg-slate-700 border-2 border-slate-700'
                                : 'border-2 border-slate-300 bg-white'
                            }`}>
                              {task.completed ? (
                                <Check size={10} strokeWidth={3} />
                              ) : isNextRequired ? (
                                <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                              ) : null}
                            </div>
                            <div className="text-left">
                              <div className={`text-sm font-medium transition-all ${
                                task.completed 
                                  ? 'text-slate-600' 
                                  : isNextRequired
                                  ? 'text-slate-900 font-semibold'
                                  : 'text-slate-900'
                              }`}>
                                {task.label}
                                {task.required && (
                                  <span className="text-slate-500 ml-2 text-xs font-normal">(Required)</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-medium transition-all ${
                              isNextRequired 
                                ? 'text-slate-700' 
                                : 'text-slate-500 group-hover:text-slate-700'
                            }`}>
                              {getActionLabel(task.id, task.completed)}
                            </span>
                            <div className={`transition-all ${
                              isNextRequired 
                                ? 'text-slate-700' 
                                : 'text-slate-400 group-hover:text-slate-600'
                            }`}>
                              <ArrowRight size={14} />
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Enhanced Dynamic CTA Button */}
                  <div className="text-center">
                    {(() => {
                      const nextRequiredTask = smartNextStep.accountSetupResult.subTasks
                        .find(task => task.required && !task.completed);
                      
                      if (nextRequiredTask) {
                        const actionLabels = {
                          company: 'Complete Company Info',
                          email: 'Finish Email Setup'
                        };
                        
                        return (
                          <button
                            onClick={() => {
                              setCTALoading(true);
                              setTimeout(() => {
                                const sectionMap = {
                                  company: 'company',
                                  email: 'email'
                                };
                                navigate(`/settings#${sectionMap[nextRequiredTask.id]}`);
                                setCTALoading(false);
                              }, 200);
                            }}
                            disabled={ctaLoading}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-800 text-white font-medium rounded-lg hover:bg-slate-900 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                          >
                            {ctaLoading ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Loading...
                              </>
                            ) : (
                              <>
                                {actionLabels[nextRequiredTask.id]} →
                              </>
                            )}
                          </button>
                        );
                      } else {
                        return (
                          <button
                            onClick={() => navigate('/settings#company')}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-800 text-white font-medium rounded-lg hover:bg-slate-900 transition-all duration-200 text-sm"
                          >
                            Finish Account Setup →
                          </button>
                        );
                      }
                    })()}
                  </div>
                </div>
              ) : (
                /* Standard CTA Card */
                <div className="bg-slate-50 rounded-xl px-10 py-9 border border-slate-200">
                  <div className="text-center">
                    <h2 className="text-xl font-semibold text-slate-900 mb-4">
                      {smartNextStep.title}
                    </h2>
                    <p className="text-slate-600 mb-9 leading-relaxed">
                      {smartNextStep.description}
                    </p>
                    
                    <div className="flex flex-col items-center gap-3">
                      <button
                        onClick={smartNextStep.onClick}
                        disabled={ctaLoading}
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-slate-800 text-white font-normal rounded-lg hover:bg-slate-900 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {ctaLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            {smartNextStep.action}
                            <ArrowRight size={16} />
                          </>
                        )}
                      </button>
                      
                      {/* Skip Link */}
                      <button
                        onClick={() => navigate('/dashboard')}
                        className="text-xs text-slate-300 hover:text-slate-500 transition-colors"
                      >
                        Skip setup
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Stats Section - Reduced Visual Weight */}
          <div className="bg-slate-50 rounded-xl p-8 border border-slate-100">
            <div className="text-center">
              <h3 className="text-base font-medium text-slate-700 mb-3">
                Your Business Insights
              </h3>
              <p className="text-slate-400 mb-6 text-sm">
                Detailed analytics and metrics will appear here once you start sending invoices
              </p>
              
              {/* Minimal Preview */}
              <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                <div className="p-4 bg-white rounded-lg border border-slate-100">
                  <div className="text-xs text-slate-400 mb-1">Revenue</div>
                  <div className="text-lg font-semibold text-slate-300">—</div>
                </div>
                <div className="p-4 bg-white rounded-lg border border-slate-100">
                  <div className="text-xs text-slate-400 mb-1">Invoices</div>
                  <div className="text-lg font-semibold text-slate-300">—</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // ACTIVE/POWER USER DASHBOARD - 2026 PREMIUM REDESIGN
  // ═══════════════════════════════════════════════════════════
  
  // Navigation handler
  const onNavigate = (page, params) => {
    if (page === 'invoices' && params?.invoiceId) {
      navigate(`/invoices?id=${params.invoiceId}`);
    } else {
      navigate(`/${page}`);
    }
  };

  return (
    <div className="h-full overflow-auto bg-white relative">
      <div className="relative max-w-[1400px] mx-auto px-8 py-8">
        
        {/* Header - Integrated, operational */}
        <div className="flex items-center justify-between mb-8 pb-6 border-b border-slate-100">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-[-0.01em] mb-1">
              Dashboard
            </h1>
            <p className="text-sm text-slate-500">
              {dashboardState === 'power' 
                ? "Your business at a glance" 
                : "Keep building momentum"
              }
            </p>
          </div>
          <div className="flex items-baseline gap-2">
            <div className="text-2xl font-semibold text-slate-900 tabular-nums">
              {onboardingProgress.totalInvoicesSent}
            </div>
            <div className="text-xs text-slate-500">
              invoices sent
            </div>
          </div>
        </div>

        {/* Revenue section - Calm operational intelligence */}
        <div className="mb-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Revenue analytics - Compact, grounded */}
            <div className="lg:col-span-2">
              <div className="bg-slate-50/30 rounded-xl p-4 border border-slate-200/30">
                
                {/* Header - Minimal, editorial */}
                <div className="flex items-center justify-between mb-2.5">
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-medium text-slate-900">
                      Revenue
                    </h2>
                    <div className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      enhancedMetrics.revenueGrowth > 0 
                        ? 'bg-emerald-50 text-emerald-700' 
                        : 'bg-rose-50 text-rose-700'
                    }`}>
                      {enhancedMetrics.revenueGrowth > 0 ? '+' : ''}{enhancedMetrics.revenueGrowth.toFixed(1)}%
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-400">Last 6 months</div>
                </div>
                
                {/* Chart - Further reduced height, grounded */}
                <div className="mb-2.5">
                  <RevenueChart data={revenueTrendData} height={100} />
                </div>
                
                {/* Integrated metrics footer - Embedded, tight */}
                <div className="flex items-center gap-4 pt-2.5 border-t border-slate-200/25">
                  <div>
                    <div className="text-[9px] text-slate-500 mb-0.5">Total</div>
                    <div className="text-sm font-semibold text-slate-900 tabular-nums">
                      {formatCurrency(revenueTrendData.reduce((sum, d) => sum + d.value, 0))}
                    </div>
                  </div>
                  <div className="w-px h-6 bg-slate-200/40" />
                  <div>
                    <div className="text-[9px] text-slate-500 mb-0.5">Average</div>
                    <div className="text-sm font-semibold text-slate-900 tabular-nums">
                      {formatCurrency(revenueTrendData.reduce((sum, d) => sum + d.value, 0) / revenueTrendData.length)}
                    </div>
                  </div>
                  <div className="w-px h-6 bg-slate-200/40" />
                  <div>
                    <div className="text-[9px] text-slate-500 mb-0.5">Best</div>
                    <div className="text-sm font-semibold text-emerald-600 tabular-nums">
                      {formatCurrency(Math.max(...revenueTrendData.map(d => d.value)))}
                    </div>
                  </div>
                  
                  {/* Contextual note for sparse data - inline */}
                  {revenueTrendData.length <= 3 && (
                    <>
                      <div className="flex-1" />
                      <div className="text-[9px] text-slate-400">
                        Based on recent activity
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {/* Key metrics - Compact, aligned */}
            <div className="bg-slate-50/30 rounded-xl p-4 border border-slate-200/30 flex flex-col justify-center space-y-3.5">
              <div>
                <div className="text-[9px] text-slate-500 mb-1">Outstanding</div>
                <div className="text-lg font-semibold text-slate-900 tabular-nums mb-0.5">
                  {formatCurrency(enhancedMetrics.totalOutstanding)}
                </div>
                <div className="text-[9px] text-slate-500">Pending collection</div>
              </div>
              
              <div className="h-px bg-slate-200/40" />
              
              <div>
                <div className="text-[9px] text-slate-500 mb-1">This month</div>
                <div className="text-lg font-semibold text-slate-900 tabular-nums mb-0.5">
                  {formatCurrency(enhancedMetrics.revenueThisMonth)}
                </div>
                <div className={`text-[9px] font-medium ${
                  enhancedMetrics.revenueGrowth > 0 ? 'text-emerald-600' : 'text-rose-600'
                }`}>
                  {enhancedMetrics.revenueGrowth > 0 ? '+' : ''}{enhancedMetrics.revenueGrowth.toFixed(1)}% vs last month
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Operational metrics - Denser, integrated */}
        <div className="mb-8">
          <div className="bg-slate-50/30 rounded-xl p-5 border border-slate-200/30">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <div className="text-xs text-slate-500 mb-1.5">Overdue</div>
                <div className={`text-xl font-semibold tabular-nums mb-0.5 ${
                  enhancedMetrics.overdueCount > 0 ? 'text-rose-600' : 'text-slate-900'
                }`}>
                  {enhancedMetrics.overdueCount}
                </div>
                <div className="text-xs text-slate-500">
                  {enhancedMetrics.overdueCount > 0 ? 'need follow-up' : 'on track'}
                </div>
              </div>
              
              <div>
                <div className="text-xs text-slate-500 mb-1.5">Collection rate</div>
                <div className={`text-xl font-semibold tabular-nums mb-0.5 ${
                  enhancedMetrics.collectionRate > 80 ? 'text-emerald-600' : 'text-amber-600'
                }`}>
                  {enhancedMetrics.collectionRate.toFixed(0)}%
                </div>
                <div className="text-xs text-slate-500">on-time</div>
              </div>
              
              <div>
                <div className="text-xs text-slate-500 mb-1.5">Payment success</div>
                <div className={`text-xl font-semibold tabular-nums mb-0.5 ${
                  enhancedMetrics.paymentSuccessRate > 70 ? 'text-emerald-600' : 'text-amber-600'
                }`}>
                  {enhancedMetrics.paymentSuccessRate.toFixed(0)}%
                </div>
                <div className="text-xs text-slate-500">success rate</div>
              </div>
              
              <div>
                <div className="text-xs text-slate-500 mb-1.5">Payment time</div>
                <div className="text-xl font-semibold text-slate-900 tabular-nums mb-0.5">
                  {enhancedMetrics.averagePaymentTime}d
                </div>
                <div className="text-xs text-slate-500">avg days</div>
              </div>
            </div>
          </div>
        </div>

        {/* Insights - Intelligent, contextual */}
        {businessInsights.length > 0 && (
          <div className="mb-8">
            <div className="space-y-2">
              {businessInsights.slice(0, 2).map((insight, index) => (
                <div 
                  key={index}
                  className="bg-blue-50/40 rounded-lg p-4 border border-blue-100/50 hover:bg-blue-50/60 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-900 mb-0.5">
                        {insight.title}
                      </div>
                      {insight.description && (
                        <div className="text-sm text-slate-600 leading-relaxed">
                          {insight.description}
                        </div>
                      )}
                    </div>
                    {insight.action && (
                      <button
                        onClick={() => onNavigateToInvoices()}
                        className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors flex-shrink-0 cursor-pointer"
                      >
                        {insight.action} →
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Operational queue and activity - Dense, workflow-oriented */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* What's next - Lightweight operational queue */}
          <div>
            <h3 className="text-sm font-medium text-slate-900 mb-3">What's next</h3>
            {nextActions.length > 0 ? (
              <div className="space-y-1">
                {nextActions.map((action) => (
                  <button
                    key={action.id}
                    onClick={() => onNavigateToInvoices()}
                    className="w-full flex items-center justify-between py-2.5 px-3 hover:bg-slate-50 
                      rounded-lg transition-colors text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <div className={`w-1 h-1 rounded-full flex-shrink-0 ${
                        action.type === 'urgent' ? 'bg-rose-500' : 
                        action.type === 'action' ? 'bg-amber-500' : 'bg-blue-500'
                      }`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-900">
                          {action.message}
                        </div>
                        {action.context && (
                          <div className="text-xs text-slate-500 mt-0.5">
                            {action.context}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-xs font-medium text-slate-500 group-hover:text-slate-900 transition-colors flex-shrink-0 ml-3">
                      {action.action} →
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center text-sm text-slate-400">
                All caught up
              </div>
            )}
          </div>
          
          {/* Activity - Refined feed */}
          <div>
            <h3 className="text-sm font-medium text-slate-900 mb-3">Recent activity</h3>
            {recentActivity.length > 0 ? (
              <div className="space-y-1">
                {recentActivity.map((activity, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between py-2.5 px-3 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <div className={`w-1 h-1 rounded-full flex-shrink-0 ${
                        activity.type === 'payment' ? 'bg-emerald-500' : 'bg-blue-500'
                      }`} />
                      <div className="text-sm text-slate-700 min-w-0 flex-1 truncate">
                        <span className="font-medium">{activity.invoice}</span>
                        <span className="text-slate-400 mx-1">·</span>
                        <span className="text-slate-500">{activity.client}</span>
                      </div>
                    </div>
                    <div className="text-sm font-medium text-slate-900 tabular-nums flex-shrink-0 ml-3">
                      {formatCurrency(activity.amount)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center text-sm text-slate-400">
                No recent activity
              </div>
            )}
          </div>
        </div>
        
        {/* Drafts - Compact operational rows */}
        {draftInvoices.length > 0 && (
          <div className="mt-8 pt-8 border-t border-slate-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-slate-900">Drafts</h3>
              <button
                onClick={onNavigateToInvoices}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors cursor-pointer"
              >
                View all →
              </button>
            </div>
            <div className="space-y-1">
              {draftInvoices.slice(0, 5).map((invoice) => (
                <button
                  key={invoice.id}
                  onClick={() => onNavigate('invoices', { invoiceId: invoice.id })}
                  className="w-full flex items-center justify-between py-2.5 px-3 hover:bg-slate-50 
                    rounded-lg transition-colors text-left cursor-pointer"
                >
                  <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    <div className="w-1 h-1 rounded-full bg-slate-300 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {invoice.invoice_number}
                      </div>
                      {invoice.client_name && (
                        <div className="text-xs text-slate-500 truncate">
                          {invoice.client_name}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-sm font-medium text-slate-900 tabular-nums ml-3 flex-shrink-0">
                    {formatCurrency(invoice.total)}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}